/**
 * Le due serrature della sezione Limulo, tenute fuori da `env.ts` e da `router.tsx` per
 * poterle esercitare senza `import.meta.env` — e perche' la decisione «quando si vede» e
 * la decisione «cosa si dice quando non si vede» sono la stessa materia.
 */
import type { LimuloVerdict } from './errors.ts';

/**
 * ⚠️ IL FLAG NON HA UNA FUNZIONE QUI, e la sua assenza e' voluta.
 *
 * `featureLimuloEnabled` e' scritto LETTERALE in `src/env.ts`
 * (`import.meta.env.VITE_FEATURE_LIMULO_ENABLED === 'true'`) come tutti gli altri flag del
 * repo, perche' cosi' il bundler lo piega a compile-time e sa gia' che la sezione e'
 * spenta. Avvolgerlo in una funzione lo renderebbe una decisione di runtime — e per il
 * token di sviluppo la stessa sostituzione e' arrivata a lasciare il segreto nel bundle
 * (v. la nota in `config.ts`). La regola resta OPT-IN: `=== 'true'` e nient'altro, nessuna
 * gentilezza su `'1'` o `'TRUE'`, perche' un flag che accetta molte forme e' un flag che
 * qualcuno accende per sbaglio.
 */

/**
 * Il verdetto → la famiglia di frasi da mostrare.
 *
 * Le famiglie sono molte e non una: un unico «errore generico» rimetterebbe a schermo
 * esattamente l'indistinguibilita' che `errors.ts` ha smontato. Le scelte che meritano
 * una parola:
 *
 *  · `app-off` e `capability-off` parlano come un 424 — per chi guarda, «il backend non
 *    serve ancora questa cosa» e' letteralmente vero in tutti e tre i casi, e la
 *    differenza (un interruttore contro un provider assente) e' materia di chi gestisce
 *    l'organizzazione, non di chi usa l'app;
 *  · `switch-off` (l'interruttore per-rotta, `E_QLASH_ENDPOINT_DISABLED`) ha una famiglia SUA
 *    e non riusa ne' `forbidden` ne' `notServed`: «Richiesta rifiutata» detto dopo un invio
 *    in chat, un referto o un'iscrizione si legge «non sei ammesso», e «non ancora servito»
 *    promette un arrivo che nessuno ha deciso. La frase e' neutra — «questa funzione non e'
 *    attiva su questo ambiente» — non dice QUALE interruttore (`settingsKey` resta ai log) e
 *    non promette che riprovare serva;
 *  · `email-not-verified` NON e' «non servito»: e' una cosa che l'utente puo' sistemare;
 *  · `identity-provider-down` sta con i guasti di server perche' e' un guasto — di un
 *    server che non e' nostro, ed e' quello che la frase dice;
 *  · `org-required` e `org-mismatch` finiscono in `unknown` di proposito: sono errori di
 *    configurazione del client, e non esiste una frase utile da dare all'utente per una
 *    cosa che deve sistemare chi ha scritto la app.
 */
const COPY_BY_VERDICT: Record<LimuloVerdict, string> = {
  // ⚠️ Copy PROVVISORIA in attesa di una scelta di prodotto (portata a Luca da
  // tma-orcr): deve reggere due letture molto diverse — «questo torneo non esiste» e
  // «nessuno serve questa richiesta» — perche' il BFF le rende indistinguibili. Chi
  // arriva da un deep-link a un torneo cancellato NON puo' leggere «riprova piu' tardi».
  'domain-or-missing': 'degraded.domainOrMissing',
  'app-off': 'degraded.notServed',
  'capability-off': 'degraded.notServed',
  forbidden: 'degraded.forbidden',
  'switch-off': 'degraded.switchOff',
  'email-not-verified': 'degraded.forbidden',
  unauthenticated: 'degraded.auth',
  'external-token-invalid': 'degraded.auth',
  network: 'degraded.network',
  // ⛔ NON 'degraded.network': quella frase dice «Controlla la connessione», che per una
  // scadenza decisa da noi accusa l'utente di un guasto che potrebbe non avere.
  timeout: 'degraded.timeout',
  server: 'degraded.server',
  'identity-provider-down': 'degraded.server',
  // Non accusa il server di essere rotto, perche' potrebbe non esserlo: v. la nota su
  // `internal-or-switch-off` in errors.ts.
  'internal-or-switch-off': 'degraded.internalOrSwitch',
  'not-found': 'degraded.notFound',
  // Le tre buste delle SCRITTURE (referto/check-in/ritiro), aggiunte il 2026-09-06.
  // ⚠️ Ognuna ha la sua famiglia e nessuna riusa `degraded.unknown`: sono gli unici rifiuti
  // di scrittura che arrivano con un motivo leggibile, e collassarli su «esito non
  // riconosciuto» butterebbe via l'unica informazione azionabile che il BFF ci da'.
  // `conflict` non parla di guasti: dice che qualcun altro e' arrivato prima, che non e'
  // un errore dell'utente e non si ritenta.
  conflict: 'degraded.conflict',
  'payload-too-large': 'degraded.fileTooLarge',
  'unsupported-media': 'degraded.fileType',
  // 400 e 422: DATI RIFIUTATI. Non e' un guasto e non e' «non riconosciuto»: dice all'utente di
  // controllare cio' che ha scritto (e, se c'e' un token di verifica, di prenderne uno nuovo).
  // 🔴 [M 2026-09-23 17:14-17:15Z, log BFF letto da limulo-core-orc] Il Relink del profilo CR di
  // Luca su stg-tma: 4 × PATCH /v1/games/profiles/:id → 400 (il provider: token CR non valido per
  // quel tag — monouso, riusato). A schermo diceva «Couldn't update the profile. Unrecognised
  // outcome», perche' `bad-request` finiva su `degraded.unknown`: un rifiuto chiaro reso muto.
  unprocessable: 'degraded.unprocessable',
  'org-required': 'degraded.unknown',
  'org-mismatch': 'degraded.unknown',
  'bad-request': 'degraded.unprocessable',
  unknown: 'degraded.unknown',
};

export function degradedCopyKey(verdict: LimuloVerdict): string {
  return COPY_BY_VERDICT[verdict] ?? 'degraded.unknown';
}

/**
 * Il contesto in cui la frase compare. Serve a UN solo verdetto — v. sotto.
 */
export type DegradedContext =
  | 'tournament'
  | 'list'
  | 'standings'
  | 'matches'
  | 'match'
  | 'profiles'
  /** La chat dell'incontro (eFootball T3): il suo degrado e' LOCALE, dentro la scheda che resta in piedi. */
  | 'chat';

/**
 * ⚠️ IL 424 HA SOLO UN TITOLO, E CORTO. Decisione di Luca (2026-08-31).
 *
 * Il 424 collassa «questo torneo non esiste» e «nessuno serve questa richiesta»
 * (`router.js:210-232`) e da qui non sappiamo quale delle due sia. Le due tentazioni
 * sono entrambe sbagliate: spiegare a schermo le due cause mette addosso all'utente un
 * dubbio che e' NOSTRO; dire «riprova piu' tardi» promette un ritorno che per meta' dei
 * casi non arrivera' mai. Resta una frase breve e neutra, che non promette nulla.
 *
 * La spiegazione lunga vive qui, nel codice, dove serve a chi mantiene — non in un
 * riquadro sotto il naso di chi voleva vedere un torneo.
 *
 * E il titolo cambia col CONTESTO, perche' «questo torneo non e' disponibile» detto
 * sopra un elenco sarebbe semplicemente falso. Senza contesto resta la formula generica:
 * meglio vaga che specifica a caso.
 */
const TITLE_ONLY_VERDICTS = new Set<LimuloVerdict>(['domain-or-missing']);

export function degradedCopyKeys(
  verdict: LimuloVerdict,
  context?: DegradedContext,
): { titleKey: string; bodyKey: string | null } {
  const family = degradedCopyKey(verdict);
  if (TITLE_ONLY_VERDICTS.has(verdict)) {
    return { titleKey: `${family}.${context ?? 'title'}`, bodyKey: null };
  }
  // Gli altri verdetti conservano titolo + corpo: li' la spiegazione serve davvero a chi
  // guarda — dice cosa fare, o perche' non e' colpa sua. Il contesto non li tocca.
  return { titleKey: `${family}.title`, bodyKey: `${family}.body` };
}
